import type { LedgerTransactionType, WalletOwnerType } from '@services/ledger.service';

// ── Transaction type presentation ──────────────────────────────────────────
// Colour is meaning here: money arriving reads success, money leaving reads
// danger, and a hold reads warning because it is money that exists but cannot
// be spent — the single most common cause of a confusing balance.

export const txnTypeLabel: Record<LedgerTransactionType, string> = {
  payment: 'Payment',
  hold: 'Hold',
  release: 'Release',
  settlement: 'Settlement',
  commission: 'Commission',
  delivery_fee: 'Delivery Fee',
  refund: 'Refund',
  withdrawal: 'Withdrawal',
  deposit: 'Deposit',
  transfer_in: 'Transfer In',
  transfer_out: 'Transfer Out',
  promotion_credit: 'Promotion Credit',
  promotion_debit: 'Promotion Debit',
  ad_spend: 'Ad Spend',
  ad_refund: 'Ad Refund',
  delivery_deduction: 'Delivery Deduction',
};

export const txnTypeChip: Record<LedgerTransactionType, string> = {
  payment: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  hold: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  release: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  settlement: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  commission: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  delivery_fee: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  refund: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  withdrawal: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  deposit: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  transfer_in: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  transfer_out: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  promotion_credit: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  promotion_debit: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  ad_spend: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  ad_refund: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  delivery_deduction: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
};

export const neutralChip = 'bg-[var(--bg-base)] text-[var(--text-muted)]';

export function labelTxnType(type: string): string {
  return txnTypeLabel[type as LedgerTransactionType] ?? type;
}

export function chipTxnType(type: string): string {
  return txnTypeChip[type as LedgerTransactionType] ?? neutralChip;
}

// ── Wallet presentation ────────────────────────────────────────────────────

export const ownerTypeLabel: Record<WalletOwnerType, string> = {
  system: 'Chekit platform',
  user: 'User',
  business: 'Business',
  branch: 'Branch',
};

/** Human sentence for a wallet type code: `chekit_escrow` → "Chekit escrow". */
export function humanizeCode(code: string): string {
  const spaced = code.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Plain-English note per system wallet — an operator should not need the schema. */
export const systemWalletBlurb: Record<string, string> = {
  chekit_escrow:
    'Every inbound payment lands here first and stays until the buyer confirms.',
  chekit_commission: 'Chekit platform commission, plus the delivery margin.',
  chekit_main: 'Chekit operating funds.',
  chekit_promotion: 'Chekit-funded promotions and discounts.',
  chekit_ad_revenue: 'Merchant advertising spend collected by Chekit.',
  tax_float: 'VAT/TOT set aside for remittance. Zero while merchants remit their own.',
};

// ── Arithmetic presentation ────────────────────────────────────────────────
// Inside a formula ("0.86 × 10.4651% = 0.09") a currency prefix on every term
// is noise — the operator already knows it is KES. `formatAmount` is the bare
// number; `formatCurrency` stays for the figures that stand alone.

export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** `10.4651` → "10.4651%", `20` → "20%". Trailing zeros are dropped. */
export function formatRate(percent: number | null | undefined): string {
  if (percent === null || percent === undefined || Number.isNaN(percent)) return '—';
  const fixed = percent.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return `${fixed}%`;
}

/** Commission rates arrive either as a percentage (10.4651) or as a fraction
 *  (0.104651) depending on which config row produced them. Pick the reading
 *  that actually reproduces the amount the backend computed, so the formula on
 *  screen is the one that was applied. */
export function resolvePercent(rate: number, part: number, base: number): number {
  if (!Number.isFinite(rate)) return 0;
  if (base > 0 && part > 0) {
    const implied = (part / base) * 100;
    return Math.abs(implied - rate) <= Math.abs(implied - rate * 100) ? rate : rate * 100;
  }
  return Math.abs(rate) <= 1 ? rate * 100 : rate;
}

/** Money equality with a cent of slack — NUMERIC(15,2) rounding must not be
 *  reported as a reconciliation failure. */
export function moneyEquals(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

// ── Timestamps ─────────────────────────────────────────────────────────────

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Short id for display — full ids are long and never fit a table cell. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.slice(0, 8);
}

/** A datetime-local input value → an ISO timestamp the API accepts. */
export function toIso(localValue: string): string | undefined {
  if (!localValue) return undefined;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
